import type {
  YukumlulukElleDurum,
  YukumlulukKanitTipi,
  YukumlulukSatirDurumu,
} from '@isg/validation';
import type { RozetAnlami } from '../theme';

/**
 * YASAL UYGUNLUK — ORTAK PARÇALAR (F4·S11·B).
 *
 * ⚠️ **DURUM → ROZET ANLAMI EŞLEMESİ TEK YERDE** (`gorev-parts` deseni).
 * Liste, çıktı ve ileride gelecek dashboard bloğu aynı satırı farklı renkte
 * gösterirse kullanıcı hangisine bakacağını bilemez. Bileşen **ANLAM** alır
 * (P3-b), renk `theme.ts`ten çözülür — burada renk adı geçmez.
 *
 * ⚠️ **ETİKETLER SUNUCUDAKİ ÇIKTI ETİKETLERİYLE BİREBİR AYNIDIR**
 * (`obligation-export.controller.ts YUKUMLULUK_DURUM_ETIKETI`) ve bu
 * eşitlik testle kilitlidir: ayrışırlarsa **denetimde açılan dosya ekrandan
 * farklı konuşur** (§2.36 — `RISK_SEVIYESI_ETIKETI` dersi).
 */
export const YUKUMLULUK_DURUM_ETIKETI: Record<YukumlulukSatirDurumu, string> = {
  karsilaniyor: 'Karşılanıyor',
  yaklasiyor: 'Vadesi yaklaşıyor',
  gecikmis: 'Gecikmiş',
  kayit_yok: 'Kayıt yok',
  periyot_bekliyor: 'Periyot bekliyor',
  kosul_kalkti: 'Koşul kalktı',
  takip_edilmiyor: 'Takip edilmiyor',
  modulde: 'Modülde takip ediliyor',
};

export const YUKUMLULUK_TIP_ETIKETI: Record<string, string> = {
  periyodik: 'Periyodik',
  surekli: 'Sürekli',
  modul_takipli: 'Modül takipli',
};

export const YUKUMLULUK_KAYNAK_ETIKETI: Record<string, string> = {
  mevzuat: 'Mevzuat',
  ic_prosedur: 'İç prosedür',
};

/**
 * Sekiz durumun anlamı — **P3'ün sözlüğü**:
 *   karsilaniyor     → **olumlu**
 *   yaklasiyor       → **dikkat**
 *   gecikmis         → **engel** ⚠️ *Kırmızı burada "sistem seni
 *                       durduruyor" DEMEZ* (takvim hiçbir kapıyı kapatmaz);
 *                       İSG dilinde **eksikliğin ağırlığını** söyler — risk
 *                       bandının kırmızısıyla aynı kol (P3-a, iki kollu).
 *   kayit_yok        → **gecikmiş** — İ4'ün GÖRÜNÜR BOŞLUĞU; *çalışıyor
 *                       sanılan boşluktan iyidir*, o yüzden nötr DEĞİL.
 *   periyot_bekliyor → **bilgi** — İ8: kalem SESSİZ BEKLER, bir uygunsuzluk
 *                       değildir (müşteri periyodu henüz belirlemedi).
 *   kosul_kalkti     → **bilgi** — eşik altına düştü; kimse bir şey
 *                       yapmadı ve **yapması da gerekmiyor** (`gecersiz`
 *                       görev durumunun kardeşi: OLMAYAN bir uygunsuzluk
 *                       üretmeyiz).
 *   takip_edilmiyor  → **dikkat** — ⚠️ bu bir DÜRÜSTLÜK KAYDIDIR: kalemin
 *                       kendisi eksik değil, **bizim takibimiz** eksik.
 *                       `engel` yazmak müşteriyi olmayan bir uygunsuzlukla
 *                       suçlar; `bilgi` yazmak boşluğu **görünmez** kılar.
 *   modulde          → **bilgi** — nereye bakılacağını söyler, bir durum
 *                       bildirmez (çift takip yasağı: ikinci sayaç YOK).
 */
export function yukumlulukRozetAnlami(durum: YukumlulukSatirDurumu): RozetAnlami {
  switch (durum) {
    case 'karsilaniyor':
      return 'olumlu';
    case 'yaklasiyor':
      return 'dikkat';
    case 'gecikmis':
      return 'engel';
    case 'kayit_yok':
      return 'gecikmis';
    case 'takip_edilmiyor':
      return 'dikkat';
    case 'periyot_bekliyor':
    case 'kosul_kalkti':
    case 'modulde':
      return 'bilgi';
  }
}

/**
 * TUR 8 / B2 — ELLE DURUM ETİKETLERİ (web v1 TR; enum'a Türkçe metin girmez —
 * §2.16'nın i18n kolu).
 *
 * ⚠️ **`kapsam_disi` *"uygun"*UN EŞ ANLAMLISI DEĞİLDİR:** *"bu işyerinde vinç
 * yok"* ile *"vinç var ve kontrolü yapıldı"* denetimde aynı cümle değildir.
 */
export const YUKUMLULUK_ELLE_DURUM_ETIKETI: Record<YukumlulukElleDurum, string> = {
  uygun: 'Uygun',
  uygun_degil: 'Uygun değil',
  kapsam_disi: 'Kapsam dışı',
};

/**
 * ✏️ **TUR 8 / D1 — KANIT BEKLENTİSİ ETİKETLERİ.** *"Denetimde ne
 * göstereceğim?"*
 *
 * ⚠️ **`null` İÇİN ETİKET YOKTUR ve bu bilinçlidir:** sınıflandırılmamış
 * kalem ekranda **boş** görünür ve boşluğun kendisi bilgidir (İ4). Oraya
 * *"belirsiz"* gibi bir kelime yazmak, kararın verilmiş ama içeriğinin
 * belirsiz olduğunu düşündürürdü — oysa karar **henüz verilmedi.**
 */
export const YUKUMLULUK_KANIT_TIPI_ETIKETI: Record<YukumlulukKanitTipi, string> = {
  belge: 'Belge',
  form_kaydi: 'Form kaydı',
  kayit_sayisi: 'Kayıt sayısı',
  beyan: 'Beyan',
};
